import type { WizardStepDefinition, WizardStepId } from "@/types";

/**
 * Canonical wizard step order.
 * Type selection happens on /admin/properties/new — not part of this rail.
 */
export const WIZARD_STEPS: WizardStepDefinition[] = [
  { id: "about", label: "About", headerTitle: "Tell us about your property", description: "A great name and description helps guests imagine their stay.", reorderable: false },
  { id: "location", label: "Location", headerTitle: "Where is it located?", description: "Provide the exact address so guests can find it easily.", reorderable: false },
  { id: "basics", label: "Basics", headerTitle: "Share some basics", description: "Bedrooms, guest capacity, and property size details.", reorderable: false },
  // Estate step hidden per business requirement — keep the entry, route, and component intact for re-enabling later.
  // { id: "estate", label: "Estate", headerTitle: "Describe your estate", description: "Detail the overall estate layout and environment.", optional: true, reorderable: false },
  { id: "amenities", label: "Amenities", headerTitle: "What amenities do you offer?", description: "From spa facilities to private chefs - list it all.", reorderable: false },
  { id: "photos", label: "Photos", headerTitle: "Add some photos of your property", description: "Upload high-quality photos to showcase your property.", reorderable: false },
  { id: "policies", label: "Policies", headerTitle: "Set your policies", description: "Cancellation terms and house rules guests must follow.", reorderable: false },
  { id: "importantInformation", label: "Important Information", headerTitle: "Important Information", description: "Add your own rules guests can confirm.", optional: true, reorderable: false },
  { id: "safety", label: "Safety", headerTitle: "Safety", description: "Share safety devices and considerations so guests know what to expect.", optional: true, reorderable: false },
  { id: "accessibility", label: "Accessibility", headerTitle: "Accessibility", description: "Let guests know which accessibility features are available in common areas and individual rooms.", optional: true, reorderable: false },
  { id: "host", label: "Host", headerTitle: "About the host", description: "Let guests know who they'll be staying with. A personal touch builds trust.", optional: true, reorderable: false },
  { id: "supplier", label: "Supplier", headerTitle: "Supplier details", description: "Supplier contact information and WeStay ratings for internal tracking.", optional: true, reorderable: false },
  { id: "pricing", label: "Pricing", headerTitle: "Price your property", description: "Set your nightly rates, minimum stays, and fees.", reorderable: false },
  {
    id: "rating",
    label: "Ratings",
    headerTitle: "WeStay rating",
    description: "Set the official WeStay rating for this property.",
    optional: true,
    reorderable: false,
  },
  { id: "review", label: "Review", headerTitle: "Review your listing", description: "One last look before your property goes live.", reorderable: false },
];

/** Old URLs for steps taken out of the rail — send them to the live successor. */
export const REMOVED_WIZARD_STEP_REDIRECTS: Partial<Record<WizardStepId, WizardStepId>> = {
  estate: "amenities",
};

export function liveWizardStep(requested: string | undefined): {
  id: WizardStepId;
  index: number;
} {
  const mapped = requested
    ? (REMOVED_WIZARD_STEP_REDIRECTS[requested as WizardStepId] ?? requested)
    : WIZARD_STEPS[0].id;
  const index = WIZARD_STEPS.findIndex((step) => step.id === mapped);
  if (index < 0) {
    return { id: WIZARD_STEPS[0].id, index: 0 };
  }
  return { id: WIZARD_STEPS[index].id, index };
}

export const PROPERTY_TYPES = [
  "villa",
  "chalet",
  "apartment",
  "island",
  "castle",
] as const;
